import type { Json } from "@redotech/json/json";
import * as z from "zod";
import { conditionJsonFormat } from "../return-flow/condition";

const FlowConditionLeafSchema = z
  .object({ type: z.string() })
  .passthrough()
  .superRefine((leaf, ctx) => {
    const json: Json = JSON.parse(JSON.stringify(leaf));
    try {
      conditionJsonFormat.read(json);
    } catch (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unknown condition ${leaf.type}: ${String(error)}`,
      });
    }
  });

const QuestionAnswerLeafSchema = z.strictObject({
  type: z.literal("question_answer"),
  questionId: z.string(),
  optionIds: z.array(z.string()),
});

const AiGuidanceLeafSchema = z.strictObject({
  type: z.literal("ai_guidance_result"),
  guidanceId: z.string(),
});

const ConditionLeafSchema = z.union([
  QuestionAnswerLeafSchema,
  AiGuidanceLeafSchema,
  FlowConditionLeafSchema,
]);

export type ConditionExpr =
  | z.infer<typeof ConditionLeafSchema>
  | { all: ConditionExpr[] }
  | { any: ConditionExpr[] }
  | { not: ConditionExpr };

export const ConditionExprSchema: z.ZodType<ConditionExpr> = z.lazy(() =>
  z.union([
    z.strictObject({ all: z.array(ConditionExprSchema) }),
    z.strictObject({ any: z.array(ConditionExprSchema) }),
    z.strictObject({ not: ConditionExprSchema }),
    ConditionLeafSchema,
  ]),
);

export const ruleScopes = ["item", "return"] as const;
export type RuleScope = (typeof ruleScopes)[number];

export function ruleSchema<V extends z.ZodTypeAny>(value: V) {
  return z.strictObject({
    id: z.string(),
    setId: z.string().optional(),
    scope: z.enum(ruleScopes).default("item"),
    when: ConditionExprSchema,
    value,
  });
}

export function conditionalSchema<V extends z.ZodTypeAny>(value: V) {
  return z.strictObject({ default: value, rules: z.array(ruleSchema(value)) });
}

export function accumulateSchema<V extends z.ZodTypeAny>(value: V) {
  return z.array(ruleSchema(value));
}

export interface Rule<V> {
  id: string;
  setId?: string;
  scope: RuleScope;
  when: ConditionExpr;
  value: V;
}
export interface Conditional<V> {
  default: V;
  rules: Rule<V>[];
}
export type Accumulate<V> = Rule<V>[];
